import { useState } from "react";
import { AppLayout } from "@project/components/AppLayout";
import { mockNotifications, type Notification } from "@project/data/mockData";
import {
  Bell,
  Calendar,
  Briefcase,
  Send,
  FileText,
  CheckCircle2,
  Check,
  Trash2,
  Sparkles,
  Loader2,
  Brain,
} from "lucide-react";
import { Button } from "@project/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { motion } from "framer-motion";
import {
  staggerContainer as container,
  fadeInUp as item,
} from "@project/lib/animations";
import { useAIAction } from "@project/hooks/useAIAction";
import AIResultDisplay from "@project/components/AIResultDisplay";

const typeIcons: Record<string, any> = {
  interview: Calendar,
  status: Briefcase,
  offer: Send,
  application: FileText,
};

const typeColors: Record<string, string> = {
  interview:
    "bg-[hsl(var(--status-posted-bg))] text-[hsl(var(--status-posted))]",
  status: "bg-[hsl(var(--status-onhold-bg))] text-[hsl(var(--status-onhold))]",
  offer: "bg-[hsl(var(--status-active-bg))] text-[hsl(var(--status-active))]",
  application: "bg-[hsl(var(--info))/0.1] text-[hsl(var(--info))]",
};

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState<Notification[]>([
    ...mockNotifications,
    {
      id: "n6",
      type: "interview",
      title: "Interview reminder",
      description: "Priya Sharma — HR Round tomorrow at 3:00 PM",
      time: "2h ago",
      read: false,
    },
    {
      id: "n7",
      type: "offer",
      title: "Offer letter generated",
      description: "AI-generated offer letter ready for Anita Desai",
      time: "4h ago",
      read: false,
    },
    {
      id: "n8",
      type: "status",
      title: "Candidate stage updated",
      description: "Karan Gupta moved to Screened stage",
      time: "1d ago",
      read: true,
    },
    {
      id: "n9",
      type: "application",
      title: "Bulk resume screening done",
      description: "18 resumes processed for Backend Engineer role",
      time: "2d ago",
      read: true,
    },
  ]);
  const [filter, setFilter] = useState("all");
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false);
  const aiSummary = useAIAction();

  const markAllRead = () =>
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  const markRead = (id: string) =>
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, read: true } : n))
    );
  const deleteNotif = (id: string) =>
    setNotifications(prev => prev.filter(n => n.id !== id));

  const unreadCount = notifications.filter(n => !n.read).length;
  const filtered =
    filter === "all"
      ? notifications
      : filter === "unread"
        ? notifications.filter(n => !n.read)
        : notifications.filter(n => n.type === filter);

  const tabs = [
    { key: "all", label: "All", count: notifications.length },
    { key: "unread", label: "Unread", count: unreadCount },
    {
      key: "interview",
      label: "Interviews",
      count: notifications.filter(n => n.type === "interview").length,
    },
    {
      key: "application",
      label: "Applications",
      count: notifications.filter(n => n.type === "application").length,
    },
    {
      key: "offer",
      label: "Offers",
      count: notifications.filter(n => n.type === "offer").length,
    },
  ];

  return (
    <AppLayout>
      <motion.div
        className="space-y-4"
        variants={container}
        initial="hidden"
        animate="show"
      >
        <motion.div
          variants={item}
          className="flex items-center justify-between"
        >
          <div>
            <h1 className="font-display text-lg font-bold tracking-tight text-foreground">
              Notifications
            </h1>
            <p className="mt-0.5 text-[12px] text-muted-foreground">
              {unreadCount > 0
                ? `${unreadCount} unread notifications`
                : "All caught up!"}
            </p>
          </div>
          {unreadCount > 0 && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="ai-btn h-8 gap-1.5 rounded-lg text-[12px] font-semibold"
                onClick={async () => {
                  setAiSummaryOpen(true);
                  const unread = notifications.filter(n => !n.read);
                  await aiSummary.run(`Summarize these HR notifications and provide action items:

${unread.map(n => `- [${n.type.toUpperCase()}] ${n.title}: ${n.description} (${n.time})`).join("\n")}

Provide:
1. **Quick Summary** - 2-3 sentence overview
2. **Priority Actions** - what needs immediate attention
3. **Upcoming Deadlines** - time-sensitive items
4. **Suggestions** - what to handle first and why`);
                }}
              >
                <Brain className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">AI Summary</span>
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={markAllRead}
                className="h-8 gap-1.5 rounded-lg text-[12px]"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Mark all read
              </Button>
            </div>
          )}
        </motion.div>

        {/* Tabs */}
        <motion.div
          variants={item}
          className="no-scrollbar flex items-center gap-0.5 overflow-x-auto rounded-xl bg-muted/50 p-0.5"
        >
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-[12px] font-medium transition-all ${filter === tab.key ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >
              {tab.label}
              <span
                className={`rounded-md px-1.5 py-0 text-[10px] font-bold ${filter === tab.key ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </motion.div>

        {/* Notification List */}
        <motion.div variants={item} className="space-y-1.5">
          {filtered.length === 0 ? (
            <div className="section-card p-8 text-center">
              <Bell className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
              <p className="text-[13px] text-muted-foreground">
                No notifications
              </p>
            </div>
          ) : (
            filtered.map(n => {
              const Icon = typeIcons[n.type] || Bell;
              return (
                <div
                  key={n.id}
                  className={`section-card flex items-start gap-3 p-3 transition-all sm:p-4 ${!n.read ? "border-l-[3px] border-l-primary" : ""}`}
                >
                  <div
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${typeColors[n.type] || "bg-muted text-muted-foreground"}`}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p
                          className={`text-[13px] ${!n.read ? "font-semibold" : "font-medium"} text-foreground`}
                        >
                          {n.title}
                        </p>
                        <p className="mt-0.5 text-[11px] text-muted-foreground">
                          {n.description}
                        </p>
                      </div>
                      <span className="mt-0.5 shrink-0 text-[10px] text-muted-foreground">
                        {n.time}
                      </span>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-0.5">
                    {!n.read && (
                      <button
                        onClick={() => markRead(n.id)}
                        className="rounded-lg p-1.5 transition-colors hover:bg-muted"
                        title="Mark read"
                      >
                        <Check className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    )}
                    <button
                      onClick={() => deleteNotif(n.id)}
                      className="rounded-lg p-1.5 transition-colors hover:bg-destructive/10"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </motion.div>
      </motion.div>

      {/* AI Summary Dialog */}
      <Dialog open={aiSummaryOpen} onOpenChange={setAiSummaryOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Brain className="h-5 w-5 text-primary" />
              AI Notification Summary
            </DialogTitle>
          </DialogHeader>
          {aiSummary.loading ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-[13px] text-muted-foreground">
                Summarizing notifications...
              </p>
            </div>
          ) : (
            <AIResultDisplay content={aiSummary.result} />
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default NotificationsPage;
